import type { ThemePreference } from '@/state/preferencesStore';

/**
 * Messages between the open tabs of one browser, so they behave as one
 * session (WEB-011, data-model.md B3). BroadcastChannel never delivers a
 * message back to the channel object that posted it.
 */
export type TabMessage =
  | { type: 'session:signed-in' }
  | { type: 'session:signed-out' }
  | { type: 'messages:read'; conversationId: string }
  | { type: 'prefs:theme'; theme: ThemePreference }
  | { type: 'call:taken'; callId: string };

const TAB_MESSAGE_TYPES = new Set<TabMessage['type']>([
  'session:signed-in',
  'session:signed-out',
  'messages:read',
  'prefs:theme',
  'call:taken',
]);

export interface TabChannel {
  post(message: TabMessage): void;
  subscribe(listener: (message: TabMessage) => void): () => void;
  close(): void;
}

function isTabMessage(value: unknown): value is TabMessage {
  return (
    !!value &&
    typeof value === 'object' &&
    TAB_MESSAGE_TYPES.has((value as { type?: TabMessage['type'] }).type as TabMessage['type'])
  );
}

export function createTabChannel(name = 'kindra'): TabChannel {
  if (typeof BroadcastChannel === 'undefined') {
    return { post: () => undefined, subscribe: () => () => undefined, close: () => undefined };
  }
  const channel = new BroadcastChannel(name);
  const listeners = new Set<(message: TabMessage) => void>();
  channel.onmessage = (event: MessageEvent) => {
    if (isTabMessage(event.data)) {
      listeners.forEach((listener) => listener(event.data as TabMessage));
    }
  };
  return {
    post: (message) => channel.postMessage(message),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    close: () => {
      listeners.clear();
      channel.close();
    },
  };
}

/** This tab's channel to the other Kindra tabs. */
export const tabs: TabChannel = createTabChannel('kindra');
